/**
 * Admin — Invite User Modal (DEV-144)
 *
 * Simple dialog for directly inviting a user by email.
 * Adds the email to the allowed_emails table (and auto-approves
 * any pending waitlist entry for that email).
 *
 * English only — system admin UI.
 */

import React, { useState } from 'react';
import { inviteUser } from '../../api/waitlist';
import styles from './InviteUserModal.module.css';

interface InviteUserModalProps {
  onClose: () => void;
  onInvited: () => void;
}

export default function InviteUserModal({ onClose, onInvited }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setSubmitting(true);
    setError(null);

    try {
      await inviteUser(trimmed, displayName.trim() || undefined);
      setSuccess(true);
      setTimeout(() => {
        onInvited();
        onClose();
      }, 1200);
    } catch (err: any) {
      setError(err?.message || 'Failed to add email to the allow-list.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.title}>Invite User</h3>
        <p className={styles.description}>
          Add an email to the allow-list so they can register for the platform.
        </p>

        {success ? (
          <div className={styles.success}>
            {email} added to the allow-list — they can now register. No email
            is sent; share the sign-in link with them directly.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <label className={styles.label}>
              Email address *
              <input
                type="email"
                className={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                autoFocus
              />
            </label>

            <label className={styles.label}>
              Display name (optional)
              <input
                type="text"
                className={styles.input}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Smith"
              />
            </label>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.inviteBtn}
                disabled={submitting || !email.trim()}
              >
                {submitting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
